/**
 * Story 5.3 — WCAG 2.1 AA contrast utility (frontend).
 *
 * Single canonical implementation of the WCAG 2.1 §1.4.3 contrast formula
 * for the `web/` workspace. Pure functions, zero dependencies, no DOM/React
 * imports — safe to consume from tests, components, and future consumers.
 *
 * Public API (named exports only — no default export):
 *   - HEX_6_RE          strict 6-digit hex regex (locked input contract)
 *   - relativeLuminance(hex)        WCAG 2.1 §1.4.3 relative luminance
 *   - contrastRatio(a, b)           WCAG 2.1 §1.4.3 contrast ratio in [1, 21]
 *   - meetsAA(a, b, large?)         AA threshold check (4.5:1 normal, 3:1 large/UI)
 *
 * Strict input contract (Critical Scope Decision #5): every public function
 * that accepts a color string requires a 6-digit hex like "#0B0E13". Any
 * other shape (3-digit shorthand, var(...), rgb(...), whitespace, empty
 * string, non-hex chars) throws an Error containing the literal substring
 * "6-digit hex" so callers can match `toThrow(/6-digit hex/)`.
 *
 * Cross-references:
 *   - Story 5.1's `web/src/theme/__tests__/tokens.contrast.test.ts` was the
 *     previous home of these helpers (declared inline). Story 5.3 extracts
 *     them here; that test file now imports from this module.
 *   - Story 6.3 will ship a parallel server-side module at
 *     `api/lib/theme/contrast.ts` that owns the lightness-shift adjustment
 *     and brand-token derivation per architecture §2.4. Drift detection is
 *     via shared WCAG fixtures at `web/src/lib/__tests__/wcag-fixtures.json`.
 *
 * AC line 980 (epics.md Story 5.3): exports relativeLuminance(hex),
 * contrastRatio(a, b), meetsAA(a, b, large?).
 *
 * References:
 *   - WCAG 2.1 §1.4.3 Contrast (Minimum):
 *     https://www.w3.org/TR/WCAG21/#contrast-minimum
 *   - WCAG 2.1 §1.4.11 Non-text Contrast:
 *     https://www.w3.org/TR/WCAG21/#non-text-contrast
 */

/**
 * Strict 6-digit hex regex. Accepts `#RRGGBB` exactly; rejects 3-digit
 * shorthand, leading/trailing whitespace, CSS function values, and
 * non-hex characters. Exported as a `readonly` named symbol so consumers
 * (e.g. Story 5.1's `tokens.contrast.test.ts` row-level assertions) reuse
 * the exact pattern without re-declaring it.
 */
export const HEX_6_RE: RegExp = /^#[0-9A-Fa-f]{6}$/;

// Module-level WCAG 2.1 AA thresholds. NOT exported — internal constants
// so the function bodies read self-documentingly (`>= THRESHOLD_NORMAL_TEXT`
// instead of a magic number). See WCAG 2.1 §1.4.3 (4.5:1 normal text) and
// §1.4.11 (3:1 UI components / large text).
const THRESHOLD_NORMAL_TEXT = 4.5;
const THRESHOLD_LARGE_TEXT_OR_UI = 3.0;

/**
 * Computes the WCAG 2.1 §1.4.3 relative luminance of an sRGB color.
 *
 * @param hex - 6-digit hex string (e.g. `"#0B0E13"`). Must match {@link HEX_6_RE}.
 * @returns Relative luminance in `[0, 1]` (0 = black, 1 = white).
 * @throws {Error} If `hex` is not a 6-digit hex string. The error message
 *   contains the literal `"6-digit hex"` substring so tests can use
 *   `toThrow(/6-digit hex/)`.
 *
 * @example
 *   relativeLuminance('#000000'); // → 0
 *   relativeLuminance('#FFFFFF'); // → 1
 *   relativeLuminance('#FF0000'); // → 0.2126
 */
export function relativeLuminance(hex: string): number {
  if (!HEX_6_RE.test(hex)) {
    throw new Error(
      `relativeLuminance: expected 6-digit hex like "#0B0E13", got "${hex}"`,
    );
  }
  const h = hex.replace(/^#/, '');
  const rgb = [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ].map((v) => v / 255);
  const linearize = (c: number): number =>
    c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  const [r, g, b] = rgb.map(linearize);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Computes the WCAG 2.1 §1.4.3 contrast ratio between two sRGB colors.
 * Symmetric: `contrastRatio(a, b) === contrastRatio(b, a)`.
 *
 * @param a - 6-digit hex string. Must match {@link HEX_6_RE}.
 * @param b - 6-digit hex string. Must match {@link HEX_6_RE}.
 * @returns Contrast ratio in `[1, 21]`. Identical colors produce `1`;
 *   black-on-white (or white-on-black) produces `21`.
 * @throws {Error} If either input is not a 6-digit hex string (validated
 *   transitively via {@link relativeLuminance}).
 *
 * @example
 *   contrastRatio('#FFFFFF', '#000000'); // → 21
 *   contrastRatio('#000000', '#000000'); // → 1
 */
export function contrastRatio(a: string, b: string): number {
  const [l1, l2] = [relativeLuminance(a), relativeLuminance(b)].sort(
    (x, y) => y - x,
  );
  return (l1 + 0.05) / (l2 + 0.05);
}

/**
 * Returns whether the contrast ratio between two sRGB colors meets the
 * WCAG 2.1 AA threshold for the given category.
 *
 * @param a - 6-digit hex string (foreground or background; symmetric).
 * @param b - 6-digit hex string (the other color).
 * @param large - When `true`, applies the WCAG large-text / UI-component
 *   threshold of 3.0:1 (per §1.4.3 large-text and §1.4.11 non-text).
 *   When `false` or omitted, applies the normal-text threshold of 4.5:1
 *   (per §1.4.3 minimum). Defaults to `false`.
 * @returns `true` iff the measured contrast ratio is `>=` the threshold.
 * @throws {Error} If either input is not a 6-digit hex string.
 *
 * @example
 *   meetsAA('#FFFFFF', '#000000');         // → true  (21 ≥ 4.5)
 *   meetsAA('#888888', '#FFFFFF');         // → false (≈3.54 < 4.5)
 *   meetsAA('#888888', '#FFFFFF', true);   // → true  (≈3.54 ≥ 3.0)
 */
export function meetsAA(a: string, b: string, large: boolean = false): boolean {
  const ratio = contrastRatio(a, b);
  const required = large ? THRESHOLD_LARGE_TEXT_OR_UI : THRESHOLD_NORMAL_TEXT;
  return ratio >= required;
}
